import React, { HTMLAttributes, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import find from 'lodash/find';

import { RankingReportPatent } from './types';
import { ColumnsType } from 'antd/lib/table';

import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import Button from 'antd/es/button';
import Image from 'antd/es/image';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Table from 'antd/es/table';
import message from 'antd/es/message';

import EyeOutlined from '@ant-design/icons/EyeOutlined';
import LinkOutlined from '@ant-design/icons/LinkOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import SortDescendingOutlined from '@ant-design/icons/SortDescendingOutlined';
import CheckCircleTwoTone from '@ant-design/icons/CheckCircleTwoTone';
import EditOutlined from '@ant-design/icons/EditOutlined';

import PatentSummary from './PatentSummary';
import { imageSrc, merge } from './utils';
import { fetchPatent, patentDisplayUri, patentExists } from '../../../api';
import { Link } from 'react-router-dom';
import { AppContext } from '../../../contexts';
import Toolbar from '../../Toolbar';
import DeleteButton from '../../DeleteButton';

const PatentRating: React.FC<{ rating: number }> = ({ rating }) => {
    return rating >= 3
        ? <>{rating} <CheckCircleTwoTone twoToneColor='#52c41a' /></>
        : <>{rating} </>;
};

interface DragableBodyRowProps {
    index: any,
    moveRow: (startIndex: number, endIndex: number) => void,
    [key: string]: any
}

const dragableBodyRowType = 'DragableBodyRow';

const DragableBodyRow: React.FC<DragableBodyRowProps & HTMLAttributes<HTMLElement>> = ({
    index,
    moveRow,
    className,
    style,
    ...restProps
}) => {
    const ref = useRef<HTMLTableRowElement>(null);
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: dragableBodyRowType,
        collect: monitor => {
            const { index: dragIndex }: any = monitor.getItem() || {};
            if (dragIndex === index) {
                return {};
            }
            return {
                isOver: monitor.isOver(),
                dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
            };
        },
        drop: (item: any) => {
            moveRow(item.index, index);
        },
    });
    const [, drag] = useDrag({
        type: dragableBodyRowType,
        item: { index },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
    });
    drop(drag(ref));

    return (
        <tr
            ref={ref}
            className={`${className}${isOver ? dropClassName : ''}`}
            style={{ cursor: 'move', ...style }}
            {...restProps}
        />
    );
};

const placeHolderImage = <Image
    src='error'
    fallback='data:image/png;base64,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'
/>;

const indexRender = (_1: any, _2: any, index: number) => <span>{index + 1}</span>;

const patentIdRender = (_1: any, patent: RankingReportPatent) => (
    <span>
        {patent.id}
        <Link to={patentDisplayUri(patent.id)} target='_blank'>
            <EyeOutlined style={{
                width: '24px',
                height: '24px',
                padding: '0',
                fontSize: '14px'
            }} />
        </Link>
    </span>
);

const titleRender = (_1: any, patent: RankingReportPatent) => <PatentSummary patent={patent} />;

const rankingRender = (onClick: (patent: RankingReportPatent) => void) => (rankingOutputId: number, patent: RankingReportPatent) => {
    return rankingOutputId
        ? <span>{rankingOutputId}<Button size='small' type='link' icon={<LinkOutlined />} onClick={() => onClick(patent)} /></span>
        : <span>Added by user</span>;
};

const ratingRender = (rating: number) => <PatentRating rating={rating} />;

const actionRender = (
    onPatentRemove: (patentId: string) => void,
    onPatentEdit: (patent: RankingReportPatent) => void
) => (patent: any) => (
    <Space>
        <Button size='small' onClick={() => onPatentEdit(patent)} icon={<EditOutlined />} />
        <DeleteButton size='small' onConfirm={() => onPatentRemove(patent.id)} />
    </Space>
);

interface GenerateColumnsArgs {
    onRankingLinkClick: (patent: RankingReportPatent) => void;
    onPatentEdit: (patent: RankingReportPatent) => void;
    onPatentRemove: (patentId: string) => void;
}

interface ReportEditorProps {
    patents: RankingReportPatent[],
    onRankingLinkClick: (patent: RankingReportPatent) => void,
    onPatentAdd: (patent: RankingReportPatent) => Promise<void>,
    onPatentEdit: (patent: RankingReportPatent) => void,
    onPatentMove: (startIndex: number, endIndex: number) => void,
    onPatentRemove: (id: string) => void,
    onPatentSort: () => void
}

const ReportEditor: React.FC<ReportEditorProps> = ({
    patents,
    onRankingLinkClick,
    onPatentAdd,
    onPatentEdit,
    onPatentMove,
    onPatentRemove,
    onPatentSort
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [addingPatent, setAddingPatent] = useState<boolean>(false);
    const { downloadToken } = useContext(AppContext);

    const patentPreviewRender = useCallback((_1: any, patent: RankingReportPatent) => {
        if (patent.images.length > 0) {
            return (
                <Image
                    className='ranking-image-preview'
                    preview={false}
                    src={imageSrc(patent.images[0], downloadToken?.id)}
                />
            );
        }
        return placeHolderImage;
    }, [downloadToken]);

    const generateColumns = useCallback(({ onRankingLinkClick: onRankingLinkClickCb, onPatentEdit: onPatentEditCb, onPatentRemove: onPatentRemoveCb }: GenerateColumnsArgs) => {
        return [
            {
                title: '#',
                dataIndex: 'id',
                width: 30,
                render: indexRender
            },
            {
                title: 'Preview',
                dataIndex: 'id',
                width: 175,
                render: patentPreviewRender
            },
            {
                title: 'ID',
                dataIndex: 'id',
                width: 200,
                render: patentIdRender
            },
            {
                title: 'Title',
                dataIndex: 'title',
                className: 'drag-visible',
                render: titleRender
            },
            {
                title: 'Ranking Output',
                dataIndex: 'rankingOutputId',
                width: 150,
                render: rankingRender(onRankingLinkClickCb)
            },
            {
                title: 'Rating',
                dataIndex: 'rating',
                width: 75,
                render: ratingRender
            },
            {
                title: 'Ranking',
                dataIndex: 'rank',
                width: 75,
            },
            {
                title: 'Actions',
                fixed: 'right',
                width: 75,
                render: actionRender(onPatentRemoveCb, onPatentEditCb)
            }
        ] as ColumnsType<RankingReportPatent>;
    }, [patentPreviewRender]);

    const addPatent = async (patentId: string) => {
        if (!patentId) {
            return;
        }

        if (find(patents, p => p.id === patentId)) {
            return message.warn(`Patent ${patentId} is already present in the report...`);
        }

        setAddingPatent(true);
        try {
            const patent = await fetchPatent(patentId);
            if (!patent) {
                throw new Error('Unable to fetch patent from server');
            }

            setSearchValue('');
            onPatentAdd(merge(patent, undefined, 0, 3)).then(
                () => window.scrollTo(0, document.body.scrollHeight)
            );
        } catch (error) {
            console.error(error);
            message.error(
                <>
                    Unable to add {patentId}.
                </>
            );
        } finally {
            setAddingPatent(false);
        }
    };

    return (
        <div className='ranking-report-editor'>
            <Toolbar className='ranking-report-editor-toolbar'>
                <Input.Search
                    placeholder='Add patent to report...'
                    allowClear
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    enterButton='Add'
                    disabled={addingPatent}
                    style={{ width: '30%', minWidth: '300px' }}
                    onSearch={addPatent} />
                <Button
                    type='default'
                    icon={<SortDescendingOutlined />}
                    loading={addingPatent}
                    onClick={() => onPatentSort()}>
                    Sort By Rating
                </Button>
            </Toolbar>

            <DndProvider backend={HTML5Backend}>
                <Table
                    bordered
                    columns={
                        generateColumns({
                            onRankingLinkClick,
                            onPatentEdit,
                            onPatentRemove
                        })
                    }
                    dataSource={patents}
                    rowKey='id'
                    size='small'
                    pagination={false}
                    onRow={(_, index) => ({ index, moveRow: onPatentMove }) as HTMLAttributes<HTMLElement>}
                    components={{ body: { row: DragableBodyRow } }}
                />
            </DndProvider>
        </div>
    );
};

export default ReportEditor;
